import ArticleLayout from '../../components/ArticleLayout';
import FejlfindingFigure from '../../components/figures/FejlfindingFigure';

export default function RadiatorVarmerIkke() {
  return (
    <ArticleLayout
      title="Radiatoren varmer ikke: Tjekliste fra det simple til fagmanden"
      metaTitle="Radiator varmer ikke? Tjekliste til kolde radiatorer"
      metaDescription="Varmer radiatoren ikke? Følg den praktiske tjekliste fra fastsiddende ventil og udluftning til cirkulationspumpe og anlægstryk."
      canonicalPath="/radiator-varmer-ikke"
      introLead="Når en radiator forbliver kold, skyldes det i langt de fleste tilfælde en fastsiddende ventilstift bag termostaten eller ophobet luft i toppen af radiatoren."
      introAnswer="Start altid med at motionere ventilstiften og mærke på fremløbsrøret. Først hvis flere radiatorer i boligen er kolde, eller hvis vandtrykket på varmeanlægget er faldet under det anbefalede niveau, skal man undersøge cirkulationspumpe og central varmeforsyning."
      tableOfContents={[
        { id: 'tjekliste', title: '1. Tjekliste: Fejlsøgning trin for trin' },
        { id: 'stiften', title: '2. Den fastgroede ventilstift' },
        { id: 'luft', title: '3. Luftlommer i radiatorkroppen' },
        { id: 'anlaeg', title: '4. Centralvarme og anlægstryk' },
        { id: 'fagmand', title: '5. Hvornår fagmanden skal tilkaldes' },
        { id: 'faq', title: '6. Ofte stillede spørgsmål' },
      ]}
      checklistTitle="Tjekliste: Gennemgå disse 5 punkter i rækkefølge"
      checklist={[
        {
          title: 'Tjek om fremløbsrøret overhovedet er lunt',
          description: 'Mærk med hånden på røret lige før termostaten for at afgøre, om der tilføres varmt vand frem til radiatoren.',
        },
        {
          title: 'Afmontér termostathovedet og massér ventilstiften',
          description: 'Tag termostaten af og tryk den lille metalstift forsigtigt ind med en flad genstand for at løsne kalk og snavs.',
        },
        {
          title: 'Luft radiatoren ud for opsamlet luft',
          description: 'Åbn luftskruen med en radiatornøgle, indtil der siver en jævn stråle vand ud uden hvislende luft.',
        },
        {
          title: 'Tjek anlægstrykket på varmecentralens manometer',
          description: 'Aflæs manometeret ved dit varmeanlæg og kontrollér, at trykket ligger på typisk 1,5 bar i et parcelhus.',
        },
        {
          title: 'Kontrollér om naboer eller andre rum mangler varme',
          description: 'Undersøg om fejlen er isoleret til ét rum, eller om cirkulationspumpen i ejendommen står stille.',
        },
      ]}
      diagram={<FejlfindingFigure />}
      sections={[
        {
          id: 'stiften',
          title: 'Den fastgroede ventilstift bag termostaten',
          content: (
            <>
              <p>
                Efter sommerperioden, hvor termostaten ofte har stået helt lukket på trin 0 eller ✱, gror den indvendige pakdåse og ventilstift hyppigt fast. Termostaten trykker nemlig stiften ind for at lukke for vandet. Når efteråret kommer og du drejer på termostathovedet, bliver stiften siddende inde, selvom drejegrebet står på trin 5.
              </p>
              <p>
                Løsningen koster intet værktøj ud over en almindelig skruetrækker eller bagenden af en ske. På en moderne Danfoss termostat drejes klemringen mod uret, eller unbrakoskruen løsnes, hvorefter termostathovedet kan trækkes af. Tryk på stiften gentagne gange. Den skal glide roligt ud igen af sig selv ved hjælp af den indre ventilfjeder.
              </p>
            </>
          ),
        },
        {
          id: 'luft',
          title: 'Luftlommer forhindrer vandcirkulationen',
          content: (
            <>
              <p>
                Hvis radiatoren er varm i bunden eller lunkent ved indløbet, men iskold i toppen over hele længden, er radiatorkroppen fyldt med luft. Vand er tungere end luft, så luften lægger sig som en prop i den øverste del og forhindrer det varme vand i at strømme igennem.
              </p>
              <p>
                Find radiatornøglen frem og hold en klud samt en lille beholder under luftskruen i modsatte ende af termostaten. Åbn skruen et halvt omgang mod uret. Når luften holder op med at hvisle, og der siver en jævn vandstråle ud, lukkes skruen igen.
              </p>
            </>
          ),
        },
        {
          id: 'anlaeg',
          title: 'Centralvarme, cirkulationspumpe og vandtryk',
          content: (
            <>
              <p>
                Er samtlige radiatorer i boligen kolde, ligger årsagen ikke i de enkelte ventiler. I et parcelhus bør man inspicere varmeanlægget i bryggerset eller kælderen. Hvis cirkulationspumpen er gået i stå eller sat for lavt, cirkulerer vandet slet ikke.
              </p>
              <p>
                Undersøg også trykmanometeret. Ifølge forsyningsanbefalinger skal trykket i et almindeligt enfamiliehus typisk ligge omkring <span className="font-mono">1,5 bar</span>. Hvis trykket er faldet til under <span className="font-mono">1,0 bar</span>, kan vandet ikke nå op til radiatorerne på førstesalen.
              </p>
            </>
          ),
        },
      ]}
      proCallout={{
        title: 'Hvornår skal du tilkalde en autoriseret VVS-installatør?',
        content: (
          <p>
            Hvis ventilstiften sidder så urokkeligt fast, at den ikke kan masseres fri, eller hvis der drypper vand ud omkring stiften, skal pakdåsen udskiftes. Ligeledes kræver fejl på cirkulationspumpe, trykekspansionsbeholder eller mistanke om skjulte rørskader en autoriseret fagmand. Begiv dig aldrig ud i at åbne varmerør eller skille ventilen ad under driftstryk.
          </p>
        ),
      }}
      faq={[
        {
          q: 'Hvorfor er radiatoren kold, selvom termostaten står på 5?',
          a: 'Den hyppigste årsag er, at den lille metalstift inde i ventilen har sat sig fast i lukket stilling efter sommeren. Tag termostathovedet af og massér stiften ind og ud med en flad genstand.',
        },
        {
          q: 'Skal en radiator være helt varm over det hele?',
          a: 'Nej, på et velfungerende fjernvarmeanlæg skal radiatoren være varm i toppen og kølig eller kold i bunden. Det viser, at vandet afkøles effektivt, før det ledes retur.',
        },
        {
          q: 'Hvad gør jeg, hvis alle radiatorer i huset er kolde på samme tid?',
          a: 'Tjek anlægstrykket på dit manometer ved varmecentralen. Ligger trykket under 1,0–1,5 bar, mangler der vand på systemet. Tjek også, om cirkulationspumpen kører.',
        },
        {
          q: 'Kan jeg ødelægge noget ved at tage termostaten af?',
          a: 'Nej, termostathovedet er blot en regulator, der trykker udefra på ventilen. Der kommer ikke vand ud, når du fjerner termostathovedet.',
        },
      ]}
      internalLinks={[
        { to: '/radiator-kold-i-bunden', text: 'Radiator kold i bunden og varm i toppen', desc: 'hvorfor det ofte er helt normalt' },
        { to: '/udluftning-af-radiator', text: 'Sådan lufter du en radiator ud', desc: 'komplet guide med nøgle og rækkefølge' },
        { to: '/radiator-ventil-sidder-fast', text: 'Ventilstiften sidder fast', desc: 'sådan får du stiften fri uden værktøj' },
        { to: '/radiator-tryk', text: 'Trykket i varmeanlægget er faldet', desc: 'hvad manometeret skal vise' },
      ]}
      sources={[
        { url: 'https://www.hofor.dk/erhverv/fjernvarme/spoergsmaal-svar-fjernvarme-erhverv/fjernvarmen-fungerer-ejendommens-radiatorer-ikke-varme-goer-vi/', label: 'Fjernvarmen fungerer, men ejendommens radiatorer er ikke varme - HOFOR' },
        { url: 'https://www.danfoss.com/da-dk/industries/buildings-residential/dhs/solutions-for-homeowners/saving-energy/', label: 'Gode råd til at spare på varmen | Danfoss' },
        { url: 'https://sparenergi.dk/privat/spar-energi-i-hverdagen/spar-paa-varme-og-vand-i-hjemmet', label: 'Normalt varmeforbrug i kWh (+ fjernvarme i MWh) | SparEnergi' },
      ]}
    />
  );
}
